import {createContext, useContext, useEffect, useMemo, useState} from 'react';
import {initial, isSuccess, mapRemoteData} from '../../common/domain/RemoteData.model';
import {findDefaultDeliveryAddress} from '../domain/OrderDeliveryAddress.model';
import {remoteDataWithSetter} from '../../common/utils/remoteDataWithSetter';
import {getDeliveryAddresses} from '../../address/infrastructure/AddressApi';
import {useComercialContext} from '../../comercial/ComercialContext';

/**
 * @param {Address}
 * @returns {{zip, name, defaultDeliveryAddress, location, id, mainInfo}}
 */
const mapToOrderDeliveryAddress = ({ id, name, mainInfo, location, zip, defaultDeliveryAddress }) => ({
  id, name, mainInfo, location, zip, defaultDeliveryAddress
});

const getOrderDeliveryAddresses = (actingAsNumCliente = null) => {
  return getDeliveryAddresses(actingAsNumCliente).then(remoteData =>
    mapRemoteData(remoteData, (addresses) => addresses?.map(mapToOrderDeliveryAddress))
  );
};

const OrderDeliveryAddress = createContext();

export const useOrderDeliveryAddress = () => useContext(OrderDeliveryAddress);

export const DeliveryAddressContextProvider = ({ children }) => {
  const { actingAsCliente } = useComercialContext();
  const [deliveryAddressesRemoteData, setDeliveryAddressesRemoteData] = useState(initial([]));
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  useEffect(() => {
    const numCliente = actingAsCliente?.numCliente ?? null;
    setSelectedAddressId(null);
    remoteDataWithSetter(setDeliveryAddressesRemoteData, () => getOrderDeliveryAddresses(numCliente)).then();
  }, [actingAsCliente]);

  const defaultDeliveryAddressRemoteData = useMemo(() => {
    return mapRemoteData(deliveryAddressesRemoteData, (it) => it ? findDefaultDeliveryAddress(it) : null);
  }, [deliveryAddressesRemoteData]);

  const selectedAddress = useMemo(() => {
    if (!isSuccess(deliveryAddressesRemoteData)) return null;
    if (selectedAddressId == null) return null;
    return deliveryAddressesRemoteData.value.find(a => a.id === selectedAddressId) ?? null;
  }, [deliveryAddressesRemoteData, selectedAddressId]);

  const effectiveAddressRemoteData = useMemo(() => {
    if (selectedAddress) return { ...defaultDeliveryAddressRemoteData, value: selectedAddress };
    return defaultDeliveryAddressRemoteData;
  }, [defaultDeliveryAddressRemoteData, selectedAddress]);

  return (
    <OrderDeliveryAddress.Provider value={{
      defaultDeliveryAddressRemoteData: effectiveAddressRemoteData,
      deliveryAddressesRemoteData,
      selectedAddressId,
      setSelectedAddressId,
    }}>
      {children}
    </OrderDeliveryAddress.Provider>
  );
};
